/* eslint-disable max-len */
import React, { useContext, useMemo, useState } from 'react';
import { analyticService } from 'global/services';
import cn from 'classnames';
import { tp } from 'app/utils/typograf';

import { isStandaloneApp } from 'app/utils/browser';
import { DishesSelectionContext } from 'app/containers/contexts/dishesSelection.context';
import { isGlobalLoadingVar, selectedPeriodVar } from 'app/apollo/reaction';
import { useReactiveVar, gql, useQuery } from '@apollo/client';
import { Portal } from 'react-portal';
import { DialogStatic } from 'app/components/Dialog/BR1156/DialogStatic';
import { RENDERING_CONTEXT_MENU, useDishDetails } from 'app/components/MobileDishDetails';
import css from './BasketSetsSelection.module.scss';

import { BasketSetsSelectionSlider } from './BasketSetsSelectionSlider';
import { setsWithFirstPeriodInFuture, upsellHashMap } from './BasketSetsSelection.record';

import promoBlockHeadLayer from './img/promoBlockHeadLayer.png';

export type TCurrentSetType = 'hp' | 'ny' | 'romantic' | 'maslenitsa' | 'green' | 'marchgifts' | 'march' | 'easter'
    | 'dacha' | 'squidgame' | 'film' | 'halloween' | 'hp' | 'fast' | 'sabantuy' | 'odyssey' | 'gryadka' | 'school' | 'harry'

interface IBasketSetsSelectionProps {
    setName: TCurrentSetType
    executeNeccessaryCartSaving: any
}

interface INavigationWrapperProps {
    children: React.ReactNode,
    setName: TCurrentSetType,
    isGlobalLoading: boolean,
    executeNeccessaryCartSaving: any,
}

const menuDishesQuery = gql`
    query DishesList(
        $period: String!,
        $category: ID,
        $categories: [ID],
        $tags: [ID],
        $type: menuType,
    ) {
        menuDishes(
            period: $period,
        ) {
            id
        }
    }
`;

const LinkWrapper = ({
    children, setName,
    isGlobalLoading,
    executeNeccessaryCartSaving,
}: INavigationWrapperProps) => {
    const isApp = isStandaloneApp();
    const isDeliveryBasket = window.location.pathname.includes('app/delivery');
    // @ts-expect-error
    const { href, savingDeliveryСartText = '' } = upsellHashMap.get(setName);
    const link = setsWithFirstPeriodInFuture.includes(setName)
        ? href
        : `${href}?period=${selectedPeriodVar()}`;

    const onSavingCompleted = () => {
        window.location.href = link;
    };


    if (isDeliveryBasket) {
        return (
            <div
                onClick={(e) => executeNeccessaryCartSaving(e, { deliverySavingPopupText: savingDeliveryСartText }, onSavingCompleted)}
                role="none"
                className={css.basketSetsSelectionRootButton}
            >
                {children}
            </div>
        );
    }

    return (
        <a
            href={link}
            target={!isApp ? '_blank' : undefined}
            rel="noreferrer"
            className={css.basketSetsSelectionRootButton}
        >
            {children}
        </a>
    );
};

const ButtonWrapper = ({ children, setName, isGlobalLoading }: INavigationWrapperProps) => {
    const { onOpenSelection } = useContext<any>(DishesSelectionContext);
    const { openDishDetails } = useDishDetails();
    // @ts-expect-error
    const { tag, id } = upsellHashMap.get(setName);

    const handleClick = () => {
        if (setName === 'school' && id) {
            openDishDetails(id, RENDERING_CONTEXT_MENU);
        } else {
            onOpenSelection({ tag, targetRealm: 'new_upsell' });
        }
    };

    return (
        <button
            onClick={handleClick}
            type="button"
            className={css.basketSetsSelectionRootButton}
            disabled={isGlobalLoading}
        >
            {children}
        </button>
    );
};

export const BasketSetsSelection = (props: IBasketSetsSelectionProps) => {
    const {
        setName,
        executeNeccessaryCartSaving = null,
    } = props;
    const isApp = isStandaloneApp();
    const isDeliveryBasket = window.location.pathname.includes('app/delivery');
    // @ts-expect-error
    const { href } = upsellHashMap.get(setName);
    const link = setsWithFirstPeriodInFuture.includes(setName)
        ? href
        : `${href}?period=${selectedPeriodVar()}`;

    const onSavingCompleted = () => {
        window.location.href = link;
    };

    const onConfirm = () => {
        if (executeNeccessaryCartSaving) {
            executeNeccessaryCartSaving(null, { deliverySavingPopupText: 'пасхальное меню' }, onSavingCompleted);
            return;
        }
        onSavingCompleted();
    };

    const [isDialogOpenned, setIsDialogOpenned] = useState(false);
    const isGlobalLoading = useReactiveVar(isGlobalLoadingVar);
    const {
        // @ts-expect-error
        type, title, description,
        // @ts-expect-error
        selectionDescription = '', linkDescription = '',
        // @ts-expect-error
        selectionTitle = '', linkTitle = '',
        // @ts-expect-error
        selectionDishes = [],
    } = upsellHashMap.get(setName);

    const { data } = useQuery(menuDishesQuery, {
        variables: {
            period: selectedPeriodVar(),
        },
    });

    const dishesList = useMemo(() => data?.menuDishes?.map((dish: any) => dish.id), [data]);
    const isCustomType = type === 'custom';
    const isCustomSelectionType = useMemo(
        () => dishesList?.some((dish: any) => selectionDishes.includes(dish)),
        [dishesList, selectionDishes],
    );

    const currentSetData: any = useMemo(
        () => ({
            description: {
                link: description,
                button: description,
                custom: isCustomSelectionType ? selectionDescription : linkDescription,
            },
            title: {
                link: title,
                button: title,
                custom: isCustomSelectionType ? selectionTitle : linkTitle,
            },
        }),
        [
            description, isCustomSelectionType, linkDescription, selectionDescription,
            selectionTitle, linkTitle, title,
        ],
    );

    const NavigationWrapper = useMemo(
        () => ((type === 'link' || (!isCustomSelectionType && isCustomType)) && setName !== 'school' ? LinkWrapper : ButtonWrapper),
        [type, isCustomSelectionType, isCustomType, setName],
    );

    const onClickHandler = () => {
        setIsDialogOpenned(true);
        analyticService.push({
            eventName: 'Upsell_Click',
            upsell_type: 'promo',
            banner_name: setName,
            source_name: 'basket',
        });
    };

    const onReject = () => {
        setIsDialogOpenned(false);
    };

    const backgroundClasses = cn({
        [css.basketSetsSelectionRoot]: true,
        [css[setName]]: true,
    });

    const titleClasses = cn({
        [css.basketSetsSelectionTitle]: true,
        [css[setName]]: true,
    });

    const descriptionClasses = cn({
        [css.basketSetsSelectionDescription]: true,
        [css[setName]]: true,
    });

    const linkButtonClasses = cn({
        [css.basketSetsSelectionButton]: true,
        [css[setName]]: true,
    });

    const headLayerImageClasses = cn({
        [css.headLayerImageRoot]: true,
        // [css[setName]]: true,
    });

    return (
        <div
            className={css.basketSetsSelectionRootWrapper}
            onClickCapture={onClickHandler}
            role="none"
        >
            <div className={backgroundClasses}>
                {setName === 'film' && (
                    // <div className={headLayerImageClasses} />
                    <img
                        src={promoBlockHeadLayer}
                        alt=""
                        className={headLayerImageClasses}
                    />
                )}
                <div className={css.basketSetsSelectionSliderContainer}>
                    <div className={titleClasses}>
                        {currentSetData.title[type]}
                    </div>
                    <div className={descriptionClasses}>
                        {currentSetData.description[type]}
                    </div>
                    <div className={css.basketHiddenBlock} />
                    <BasketSetsSelectionSlider setName={setName} />
                    {isCustomType && !isCustomSelectionType && setName !== 'school'
                        ? (
                            <div
                                role="none"
                                onClick={() => setIsDialogOpenned(true)}
                                className={linkButtonClasses}
                            >
                                Посмотреть меню
                            </div>

                        )
                        : (
                            <NavigationWrapper
                                setName={setName}
                                isGlobalLoading={isGlobalLoading}
                                executeNeccessaryCartSaving={executeNeccessaryCartSaving}
                            >
                                <div className={linkButtonClasses}>{setName === 'harry' ? 'Добавить блюда' : 'Посмотреть меню'}</div>
                            </NavigationWrapper>
                        )
                    }

                </div>
                {isCustomType && !isCustomSelectionType && setName !== 'school' && (
                    <Portal>
                        <DialogStatic
                            rejectText=""
                            confirmText="Ок"
                            isOpen={isDialogOpenned}
                            onConfirm={() => {
                                setIsDialogOpenned(false);
                                onConfirm();
                            }}
                            onReject={onReject}
                            textAlign="start"
                            isCloseConfirmAction={false}
                            isProfileLayout
                            // @ts-ignore
                            isNewIrresponsiveLayout
                            oneRowButtons
                            buttonsReversed
                            onlyConfirmButton
                        >
                            <div className={css.basketSetsSelectionDialogText}>
                                Важно!
                                <br />
                                {tp.execute('Пасхальное меню оформляем отдельным заказом и доставляем с 6 по 12 апреля.')}
                            </div>
                        </DialogStatic>
                    </Portal>
                )}
            </div>
        </div>
    );
};

export default BasketSetsSelection;
